// Списание долга, решения при увольнении и невозвратные потери.
//
// Решение записывается, а не подразумевается: без записи «расчёт разблокирован» означало бы
// «кто-то посчитал, что можно», и через полгода никто не вспомнит, кто отпустил долг.

import { request } from '@/shared/api/client';
import { queryString } from '@/shared/api/query';
import type { ListResponse } from '@/shared/api/types';

export type DebtDecisionKind =
  'write_off' | 'keep_active' | 'external_collection' | 'deduct_from_final';

export interface LossType {
  loss_type: string;
  title: string;
  cases: number;
  people: number;
  amount: number;
  /** Заполнено, когда строка про конкретный долг, а не итог по типу. */
  debt_id?: string | null;
}

export interface FinalSettlementOut {
  employee_id: string;
  is_fired: boolean;
  blocked: boolean;
  pending: LossType[];
  decided: number;
  reason?: string | null;
}

export interface DecisionOut {
  debt_id: string;
  employee_id: string;
  decision: string;
  basis: string;
  amount: number;
  loss_id?: string | null;
  decided_at: string;
}

/** Строка разреза потерь: по человеку либо по тому, кто признал потерю невозвратной. */
export interface LossCut {
  key: string | null;
  title: string;
  cases: number;
  amount: number;
}

export interface LossesReportOut {
  period_from: string;
  period_to: string;
  items: LossType[];
  total_amount: number;
  total_cases: number;
  /**
   * Разрезы: кто унёс и кто решил, что деньги не вернутся.
   *
   * Второе важнее первого. Списание — единственное денежное действие, которое ничего не
   * возвращает и никем не подтверждается второй стороной; через полгода спрашивают про него,
   * и до 06.09.2026 спрашивать было не с кого.
   *
   * Разреза по объектам нет намеренно: потеря принадлежит человеку, а не объекту, и привязка
   * через сегодняшнее назначение дала бы ответ на другой вопрос с видом настоящего.
   */
  by_employee?: LossCut[];
  by_decider?: LossCut[];
  /** Накоплено за всё время. Заказчик говорит про потери «уже миллионы исчисляются». */
  total_all_time?: number;
  generated_at: string;
}

export function fetchFinalSettlement(employeeId: string): Promise<FinalSettlementOut> {
  return request<FinalSettlementOut>(`/api/v1/employees/${employeeId}/final-settlement`);
}

export function decideDebt(
  debtId: string,
  decision: DebtDecisionKind,
  basis: string,
): Promise<DecisionOut> {
  return request<DecisionOut>(`/api/v1/debts/${debtId}/decision`, {
    method: 'POST',
    body: { decision, basis },
  });
}

export function writeOffDebt(debtId: string, basis: string): Promise<DecisionOut> {
  return request<DecisionOut>(`/api/v1/debts/${debtId}/write-off`, {
    method: 'POST',
    body: { basis },
  });
}

export function fetchLosses(params: { year: number; month: number }): Promise<LossesReportOut> {
  return request<LossesReportOut>(`/api/v1/reports/losses${queryString(params)}`);
}

// ── Долги человека и спецодежда ────────────────────────────────────────────────────────────

export interface DebtOut {
  id: string;
  employee_id: string;
  employee_name: string;
  legal_entity_id: string;
  debt_type: string;
  /** Название типа по-русски: словарь названий живёт в контракте, а не на экране. */
  debt_type_title: string;
  description?: string | null;
  initial_amount: number;
  outstanding: number;
  /** Способ гашения: `per_payroll`, `equal_parts`, `percent_of_payout`, `full_at_once`, `manual`. */
  schedule_type: string;
  /** Название способа по-русски: словарь названий живёт в контракте, а не на экране. */
  schedule_type_title: string;
  /**
   * Значение графика В ЕДИНИЦАХ СВОЕГО ТИПА: копейки в месяц, число месяцев или проценты.
   *
   * ⚠️ Само по себе это число читать и показывать НЕЛЬЗЯ — без типа рядом оно бессмысленно, и
   * «6» у долга с погашением за полгода экран напечатал бы как «6 ₽ в месяц». Для показа
   * есть готовая фраза `schedule_note`, собранная сервером; это поле нужно только форме,
   * чтобы подставить прежнее значение в поле ввода.
   */
  schedule_value?: number | null;
  /** Фраза для человека: «1 000 ₽ в месяц», «равными долями за 3 месяца», «график не задан». */
  schedule_note: string;
  /**
   * Сколько по долгу удержится за месяц по графику, копейки, не больше остатка: один раз на
   * месяц человека, сколько бы ведомостей у него ни было. `null` — заранее не сказать (процент
   * от суммы к выплате). Ноль — расчёт этот долг не удерживает (вручную, без графика, закрыт).
   * Необязательное: сервер до 19.10.2026 его не присылал.
   */
  month_plan?: number | null;
  status: string;
  /** Состояние по-русски: словарь названий живёт в контракте, а не на экране. */
  status_title: string;
  origin: string;
  basis?: string | null;
  created_at: string;
  /** Мешает ли долг оформлению: действующий и с остатком. */
  is_blocking: boolean;
}

export interface UniformItem {
  id: string;
  code: string;
  title: string;
  price: number;
  sort_order: number;
  /** С какого дня действует нынешняя цена. Пусто — истории цен у позиции нет. */
  price_since?: string | null;
  /** Предлагается ли позиция при выдаче. Убранная остаётся в истории и в чужих долгах. */
  is_active?: boolean;
  /**
   * Сколько раз позицию выдавали. Приезжает только со справочником настроек.
   *
   * Нужно ровно для одного: объяснить отказ ЦИФРОЙ. «Никому не выдавали» и «выдали двенадцати
   * людям» — два разных разговора с человеком, и выбрать между ними можно, только зная число.
   */
  issues_count?: number;
}

/**
 * Одно изменение цены позиции.
 *
 * Цена хранится не числом у позиции, а ОТРЕЗКОМ ВРЕМЕНИ: с какого дня действует. Цена,
 * поставленная двадцатым сентября, к девятнадцатому отношения не имеет — ни к заведённому
 * долгу, ни к пересчёту, ни к отчёту за август.
 */
export interface UniformPrice {
  id: string;
  price: number;
  effective_from: string;
  basis?: string | null;
  created_at: string;
  author?: string | null;
  /** Строку убрали как ошибочную: в цене на дату она не участвует, в истории остаётся. */
  cancelled?: boolean;
  cancel_reason?: string | null;
}

export interface UniformPriceIn {
  price: number;
  /** День, С КОТОРОГО действует. Обязателен: подстановка «сегодня» соврала бы про вчерашнее. */
  effective_from: string;
  basis?: string;
}

export function fetchUniformPrices(itemId: string): Promise<ListResponse<UniformPrice>> {
  return request<ListResponse<UniformPrice>>(
    `/api/v1/uniform/items/${encodeURIComponent(itemId)}/prices`,
  );
}

export function changeUniformPrice(itemId: string, body: UniformPriceIn): Promise<UniformItem> {
  return request<UniformItem>(`/api/v1/uniform/items/${encodeURIComponent(itemId)}/prices`, {
    method: 'POST',
    body,
  });
}

/**
 * Убрать ошибочную строку цены.
 *
 * `POST … /cancel`, а не `DELETE`: строка остаётся в истории зачёркнутой и выходит только из
 * расчёта цены на дату. Опечатку в цене чинит вторая запись тем же днём, а опечатку в ДАТЕ —
 * только это: ошибочная строка иначе продолжает отвечать за свой отрезок времени.
 */
export function cancelUniformPrice(
  itemId: string,
  priceId: string,
  reason: string,
): Promise<UniformItem> {
  return request<UniformItem>(
    `/api/v1/uniform/items/${encodeURIComponent(itemId)}/prices/${encodeURIComponent(priceId)}/cancel`,
    { method: 'POST', body: { reason } },
  );
}

export interface UniformLine {
  id: string;
  uniform_item_id?: string | null;
  title: string;
  quantity: number;
  price: number;
  amount: number;
}

export interface UniformIssueOut {
  id: string;
  employee_id: string;
  legal_entity_id: string;
  issued_at: string;
  total_amount: number;
  debt_id?: string | null;
  lines: UniformLine[];
}

export interface UniformLineIn {
  uniform_item_id?: string;
  title_manual?: string;
  quantity: number;
  price_manual?: number;
}

/** Тип долга: код для кода, название для человека. Названия приходят из контракта. */
export interface DebtTypeNameOut {
  code: string;
  title: string;
  /**
   * Сколько удерживать в месяц по умолчанию, копейки: форма 100000, билет 50000.
   *
   * Приезжает с сервера, чтобы форма не держала второй копии этих чисел: они живут в
   * настройках расчёта и попадают в снимок утверждённой ведомости. Пусто означает, что
   * умолчания нет и без явного графика такой долг не удержится ни разу.
   */
  default_schedule_value?: number | null;
}

/**
 * Способ гашения долга: чем подписать поле, что в него вводить и как это объяснить.
 *
 * Форма рисуется по этим данным целиком. Решать на экране, что спросить у «равных долей»,
 * нельзя: единица измерения — знание сервера, и экран, решивший сам, спросит рубли там, где
 * нужны месяцы.
 */
export interface DebtScheduleTypeOut {
  code: string;
  title: string;
  /** `kopecks`, `months`, `percent` или `none`, если вводить нечего. */
  value_kind: string;
  value_label: string;
  hint: string;
}

export function fetchDebtScheduleTypes(): Promise<ListResponse<DebtScheduleTypeOut>> {
  return request<ListResponse<DebtScheduleTypeOut>>('/api/v1/debt-schedule-types');
}

export function fetchDebtTypes(): Promise<ListResponse<DebtTypeNameOut>> {
  return request<ListResponse<DebtTypeNameOut>>('/api/v1/debt-types');
}

/**
 * Заведение долга вручную (сценарий приёмки 41).
 *
 * Долги обычно заводятся сами — приёмкой заявки в категории с признаком «создаёт долг» либо
 * выдачей формы по справочнику. Ручной путь нужен для того, что через заявку не проходит:
 * штраф, ремонт машины, заём от заказчика. До 26.08.2026 его не было в программе вовсе, хотя
 * дверь на сервере есть и сценарий числился закрытым.
 */
export interface DebtIn {
  employee_id: string;
  legal_entity_id: string;
  debt_type: string;
  /** Первоначальная сумма в копейках. */
  initial_amount: number;
  /** Способ гашения. Без него сервер возьмёт «фиксированная сумма в месяц». */
  schedule_type?: string;
  /** Значение в единицах своего способа: копейки, месяцы или проценты. */
  schedule_value?: number | null;
  description?: string | null;
  basis?: string | null;
}

export function createDebt(body: DebtIn): Promise<DebtOut> {
  return request<DebtOut>('/api/v1/debts', { method: 'POST', body });
}

/**
 * Смена графика у заведённого долга (тз 5.5).
 *
 * Сумму долга не трогает: суммы меняются только корректировкой. Нужна затем, что
 * договорённость с человеком меняется чаще, чем сам долг, — «давай я буду отдавать за
 * полгода» не повод заводить второй долг и терять историю удержаний по первому. Этим же
 * путём правится график у остатков, залитых пачкой при переносе.
 */
export interface DebtScheduleIn {
  schedule_type: string;
  schedule_value?: number | null;
  basis: string;
}

export function setDebtSchedule(debtId: string, body: DebtScheduleIn): Promise<DebtOut> {
  return request<DebtOut>(`/api/v1/debts/${debtId}/schedule`, { method: 'PUT', body });
}

export function fetchEmployeeDebts(employeeId: string): Promise<ListResponse<DebtOut>> {
  return request<ListResponse<DebtOut>>(`/api/v1/employees/${employeeId}/debts`);
}

/**
 * Перенесены ли долги, бывшие ДО начала работы в программе.
 *
 * Пустой список долгов в двух случаях выглядит одинаково: «никто ничего не должен» и «сюда
 * ещё ничего не переносили». Различить их может только сервер. 23.09.2026 заказчик дважды
 * прочёл «долгов нет» буквально — долгов в программе не было потому, что остатки на старт не
 * переносились вовсе.
 */
export interface OpeningBalancesOut {
  migrated: boolean;
  /** Сколько долгов пришло переносом. Только тем, кто видит долги всех. */
  debts?: number | null;
  migrated_at?: string | null;
  /** На сколько перенесено, копейки. Только тем, кто видит долги всех. */
  total?: number | null;
}

export function fetchOpeningBalances(): Promise<OpeningBalancesOut> {
  return request<OpeningBalancesOut>('/api/v1/debts/opening-balances');
}

// ── Перенос остатков листом ─────────────────────────────────────────────────────────────────

/** Строка листа остатков: кто, за что, сколько. Человека по фамилии ищет сервер. */
export interface OpeningBalanceRowIn {
  /** Строка листа как её вставили: попадает в отчёт и в описание долга. */
  line: string;
  /** Фамилия и инициалы из листа. */
  name: string;
  debt_type: string;
  /** Остаток в копейках. */
  amount: number;
  /** Выбор человека из однофамильцев. Пусто — искать по фамилии. */
  employee_id?: string | null;
}

export interface OpeningBalancesIn {
  legal_entity_id: string;
  /** Откуда остатки: пишется в каждый перенесённый долг. */
  basis: string;
  /** Только показать, ничего не записывая. */
  dry_run: boolean;
  rows: OpeningBalanceRowIn[];
}

export type OpeningBalanceRowState =
  'ready' | 'written' | 'not_found' | 'ambiguous' | 'repeated' | 'exists';

export interface OpeningBalanceRowOut {
  index: number;
  line: string;
  state: OpeningBalanceRowState;
  employee_id?: string | null;
  employee_name?: string | null;
  debt_type: string;
  debt_type_title: string;
  amount: number;
  /** Почему строка не переносится, словами. */
  reason?: string | null;
  candidates: { employee_id: string; label: string }[];
}

export interface OpeningBalanceTypeOut {
  debt_type: string;
  title: string;
  count: number;
  total: number;
  /** Удерживается ли такой долг сам: у вида есть сумма в месяц по умолчанию. */
  withheld_by_default: boolean;
}

export interface OpeningBalancesPlanOut {
  dry_run: boolean;
  rows: OpeningBalanceRowOut[];
  ready: number;
  ready_total: number;
  by_type: OpeningBalanceTypeOut[];
  summary: string;
}

export function planOpeningBalances(body: OpeningBalancesIn): Promise<OpeningBalancesPlanOut> {
  return request<OpeningBalancesPlanOut>('/api/v1/debts/opening-balances', {
    method: 'POST',
    body,
  });
}

export interface OpeningBalancesCancelOut {
  dry_run: boolean;
  cancelled: number;
  cancelled_total: number;
  kept: { employee_name: string; debt_type_title: string; outstanding: number; reason: string }[];
  summary: string;
}

export function cancelOpeningBalances(body: {
  basis: string;
  dry_run: boolean;
}): Promise<OpeningBalancesCancelOut> {
  return request<OpeningBalancesCancelOut>('/api/v1/debts/opening-balances/cancel', {
    method: 'POST',
    body,
  });
}

/** Отбор реестра долгов. Тз, раздел 5.5: по типу, состоянию, юрлицу и сотруднику. */
export interface DebtsQuery {
  employee_id?: string;
  legal_entity_id?: string;
  debt_type?: string;
  status?: string;
  /** Быстрый переключатель «только непогашенные»: не то же, что точный отбор по состоянию. */
  only_active?: boolean;
}

export function fetchDebts(params: DebtsQuery = {}): Promise<ListResponse<DebtOut>> {
  return request<ListResponse<DebtOut>>(`/api/v1/debts${queryString({ ...params })}`);
}

export interface RepaymentOut {
  id: string;
  debt_id: string;
  amount: number;
  /** Из какого расчёта удержано. Пусто — удержание вне ведомости, с основанием. */
  payroll_employee_id?: string | null;
  created_at: string;
  created_by?: string | null;
}

/** История удержаний по долгу: тз, раздел 5.5, карточка долга. */
export function fetchRepayments(debtId: string): Promise<ListResponse<RepaymentOut>> {
  return request<ListResponse<RepaymentOut>>(`/api/v1/debts/${debtId}/repayments`);
}

/**
 * Справочник спецодежды.
 *
 * `includeHidden` спрашивают ТОЛЬКО настройки, и только чтобы убранную позицию можно было
 * вернуть. Форме выдачи убранные не показываются никогда: там их и не просят.
 */
export function fetchUniformItems(includeHidden = false): Promise<ListResponse<UniformItem>> {
  return request<ListResponse<UniformItem>>(
    `/api/v1/uniform/items${includeHidden ? '?include_hidden=true' : ''}`,
  );
}

/** Новая позиция справочника: тз, раздел 5.5 — «заводит администратор в настройках». */
export interface UniformItemIn {
  /**
   * Машинный ключ позиции. **Кабинет его не шлёт: сервер выводит ключ из названия.**
   *
   * Остаётся в описании ради переноса данных заказчика — он приходит со своими ключами, и
   * менять их нельзя: по ним уже сослались заведённые долги.
   */
  code?: string;
  title: string;
  /** Цена в копейках. */
  price: number;
  sort_order?: number;
  /**
   * С какого дня действует эта цена. Не задано — с сегодняшнего.
   *
   * Позицию заводят не только для будущего: куртку пилот заказчик выдаёт два года, а в
   * справочнике её нет. Без этого поля все прошлые выдачи остались бы без цены вовсе.
   */
  price_since?: string;
}

export function createUniformItem(body: UniformItemIn): Promise<UniformItem> {
  return request<UniformItem>('/api/v1/uniform/items', { method: 'POST', body });
}

export interface UniformItemPatch {
  title?: string;
  price?: number;
  sort_order?: number;
  /** Снять позицию с выдачи. Уже выданное считается по цене на момент выдачи. */
  is_active?: boolean;
}

export function updateUniformItem(itemId: string, body: UniformItemPatch): Promise<UniformItem> {
  return request<UniformItem>(`/api/v1/uniform/items/${encodeURIComponent(itemId)}`, {
    method: 'PATCH',
    body,
  });
}

export function fetchUniformIssues(employeeId: string): Promise<ListResponse<UniformIssueOut>> {
  return request<ListResponse<UniformIssueOut>>(`/api/v1/employees/${employeeId}/uniform`);
}

export function issueUniform(
  body: {
    employee_id: string;
    /** Не задано — сервер возьмёт юрлицо из действующего назначения человека. */
    legal_entity_id?: string;
    lines: UniformLineIn[];
  },
  idempotencyKey: string,
): Promise<UniformIssueOut> {
  // Ключ операции обязателен: выдача заводит долг, и двойное нажатие «Оформить» без ключа
  // завело бы человеку два долга на одну куртку.
  return request<UniformIssueOut>('/api/v1/uniform/issues', {
    method: 'POST',
    body,
    idempotencyKey,
  });
}

// ── Билет: компенсация и досрочный отъезд ──────────────────────────────────────────────────

export interface TicketState {
  employee_id: string;
  has_ticket: boolean;
  debt_id?: string | null;
  price: number;
  outstanding: number;
  compensation_used: boolean;
  compensation_applied: boolean;
  compensation_amount: number;
  /** Удерживается разом: человек уехал, следующего расчёта у него не будет. */
  deduct_at_once: boolean;
  left_early: boolean;
  reason_confirmed: boolean;
  explanation: string;
}

export function fetchTicketState(employeeId: string): Promise<TicketState> {
  return request<TicketState>(`/api/v1/employees/${employeeId}/ticket`);
}

export function registerEarlyLeave(
  employeeId: string,
  body: { left_at: string; reason: string; reason_confirmed: boolean },
): Promise<TicketState> {
  return request<TicketState>(`/api/v1/employees/${employeeId}/early-leave`, {
    method: 'POST',
    body,
  });
}
